import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Button,
  Table,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from 'reactstrap';
import { oracleReadingService } from '../services';
import { OracleReadingOrder, OracleReadingOrderStatus } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { formatUkDateTime } from '../utils/dateFormat';

const STATUS_OPTIONS: { value: '' | OracleReadingOrderStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'paid', label: 'Accepted (ready to complete)' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'delivered', label: 'Delivered' },
];

function statusBadgeColor(status: OracleReadingOrderStatus): string {
  switch (status) {
    case 'pending_approval':
      return 'warning';
    case 'paid':
      return 'info';
    case 'rejected':
      return 'danger';
    case 'delivered':
      return 'success';
    default:
      return 'secondary';
  }
}

function renderUserQuestion(userQuestion?: string | null): React.ReactNode {
  const trimmed = userQuestion?.trim();
  if (trimmed) {
    return (
      <span className="text-body" title={trimmed}>
        {trimmed}
      </span>
    );
  }
  return <span className="text-muted fst-italic">General reading</span>;
}

function statusLabel(status: OracleReadingOrderStatus): string {
  switch (status) {
    case 'pending_approval':
      return 'Pending approval';
    case 'paid':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    case 'delivered':
      return 'Delivered';
    default:
      return status;
  }
}

const OracleReadingPage: React.FC = () => {
  const [orders, setOrders] = useState<OracleReadingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | OracleReadingOrderStatus>('');
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<OracleReadingOrder | null>(null);
  const [completeModal, setCompleteModal] = useState<OracleReadingOrder | null>(null);
  const [completeVideoFile, setCompleteVideoFile] = useState<File | null>(null);
  const [completeTitle, setCompleteTitle] = useState('');
  const [completeShortDesc, setCompleteShortDesc] = useState('');
  const [completeLongDesc, setCompleteLongDesc] = useState('');
  const [completeThumbnailFile, setCompleteThumbnailFile] = useState<File | null>(null);
  const [completing, setCompleting] = useState(false);
  const [patchModal, setPatchModal] = useState<OracleReadingOrder | null>(null);
  const [patchTitle, setPatchTitle] = useState('');
  const [patchShortDesc, setPatchShortDesc] = useState('');
  const [patchLongDesc, setPatchLongDesc] = useState('');
  const [patchVideoFile, setPatchVideoFile] = useState<File | null>(null);
  const [patchThumbnailFile, setPatchThumbnailFile] = useState<File | null>(null);
  const [patching, setPatching] = useState(false);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const list = await oracleReadingService.getOrders(
        statusFilter || undefined
      );
      setOrders(list);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const handleAccept = async (order: OracleReadingOrder) => {
    setActioningId(order.id);
    try {
      await oracleReadingService.acceptOrder(order.id);
      toast.success('Request accepted. Mark as complete when you have sent the reading.');
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to accept');
    } finally {
      setActioningId(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    const orderId = rejectModal.id;
    setRejectModal(null);
    setActioningId(orderId);
    try {
      await oracleReadingService.rejectOrder(orderId);
      toast.success('Request rejected and payment refunded.');
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActioningId(null);
    }
  };

  const openCompleteModal = (order: OracleReadingOrder) => {
    setCompleteModal(order);
    setCompleteVideoFile(null);
    setCompleteTitle('');
    setCompleteShortDesc('');
    setCompleteLongDesc('');
    setCompleteThumbnailFile(null);
  };

  const openPatchModal = (order: OracleReadingOrder) => {
    setPatchModal(order);
    setPatchTitle(order.title ?? '');
    setPatchShortDesc(order.shortDesc ?? '');
    setPatchLongDesc(order.longDesc ?? '');
    setPatchVideoFile(null);
    setPatchThumbnailFile(null);
  };

  const handlePatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patchModal) return;
    const titleTrim = patchTitle.trim();
    if (!titleTrim) {
      toast.error('Title is required.');
      return;
    }
    setPatching(true);
    try {
      await oracleReadingService.patchOrder(patchModal.id, {
        title: titleTrim,
        shortDesc: patchShortDesc,
        longDesc: patchLongDesc,
        videoFile: patchVideoFile ?? undefined,
        thumbnailFile: patchThumbnailFile ?? undefined,
      });
      toast.success('Order details updated.');
      setPatchModal(null);
      setPatchTitle('');
      setPatchShortDesc('');
      setPatchLongDesc('');
      setPatchVideoFile(null);
      setPatchThumbnailFile(null);
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update');
    } finally {
      setPatching(false);
    }
  };

  const handleCompleteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completeModal) return;
    const titleTrim = completeTitle.trim();
    if (!titleTrim) {
      toast.error('Title is required.');
      return;
    }
    if (!completeVideoFile) {
      toast.error('Please select a video file to complete the order.');
      return;
    }
    setCompleting(true);
    try {
      await oracleReadingService.completeOrder(completeModal.id, {
        videoFile: completeVideoFile,
        title: titleTrim,
        shortDesc: completeShortDesc.trim() || undefined,
        longDesc: completeLongDesc.trim() || undefined,
        thumbnailFile: completeThumbnailFile || undefined,
      });
      toast.success('Order marked complete. User has been notified.');
      setCompleteModal(null);
      setCompleteVideoFile(null);
      setCompleteTitle('');
      setCompleteShortDesc('');
      setCompleteLongDesc('');
      setCompleteThumbnailFile(null);
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to complete');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
      <BreadCrumb pageTitle="Deeper Oracle Reading" title="Oracle Reading" />
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h5 className="card-title mb-0">Deeper Oracle Reading requests</h5>
                <FormGroup className="mb-0 ms-auto">
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value as '' | OracleReadingOrderStatus
                      )
                    }
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>User question</th>
                      <th>Platform</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          <Spinner size="sm" /> Loading...
                        </td>
                      </tr>
                    ) : !orders.length ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No orders found.
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <div>
                              <strong>{order.userName || '—'}</strong>
                              <br />
                              <small className="text-muted">
                                {order.userEmail || '—'}
                              </small>
                            </div>
                          </td>
                          <td style={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {renderUserQuestion(order.userQuestion)}
                          </td>
                          <td>
                            <Badge color="info">
                              {order.platform || '—'}
                            </Badge>
                          </td>
                          <td>
                            {order.paidAt
                              ? formatUkDateTime(order.paidAt)
                              : '—'}
                          </td>
                          <td>
                            <Badge color={statusBadgeColor(order.status)}>
                              {statusLabel(order.status)}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              {order.status === 'pending_approval' && (
                                <>
                                  <Button
                                    size="sm"
                                    color="success"
                                    disabled={actioningId === order.id}
                                    onClick={() => handleAccept(order)}
                                  >
                                    {actioningId === order.id
                                      ? '...'
                                      : 'Accept'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="danger"
                                    disabled={actioningId === order.id}
                                    onClick={() => setRejectModal(order)}
                                  >
                                    {actioningId === order.id
                                      ? '...'
                                      : 'Reject'}
                                  </Button>
                                </>
                              )}
                              {order.status === 'paid' && (
                                <Button
                                  size="sm"
                                  color="primary"
                                  onClick={() => openCompleteModal(order)}
                                >
                                  Complete request
                                </Button>
                              )}
                              {order.status === 'delivered' && (
                                <>
                                  {order.fileUrl && (
                                    <a
                                      href={order.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-secondary"
                                    >
                                      View video
                                    </a>
                                  )}
                                  <Button
                                    size="sm"
                                    color="outline-primary"
                                    onClick={() => openPatchModal(order)}
                                  >
                                    Edit details
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {!loading && orders.length > 0 && (
                <p className="text-muted small mb-0 mt-2">
                  Total: {orders.length} order(s)
                </p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Reject confirmation (refund) */}
      <Modal
        isOpen={!!rejectModal}
        toggle={() => setRejectModal(null)}
        centered
      >
        <ModalHeader toggle={() => setRejectModal(null)}>
          Reject request and refund?
        </ModalHeader>
        <ModalBody>
          {rejectModal && (
            <>
              <p className="mb-2">
                Rejecting this request cannot be undone. Note: IAP purchases cannot be automatically refunded - the user must request a refund directly from the App Store or Google Play Store.
              </p>
              <p className="text-muted small mb-2">
                User: {rejectModal.userName || '—'} ({rejectModal.userEmail || '—'})
              </p>
              <p className="small mb-0">
                <strong>User question:</strong>{' '}
                {renderUserQuestion(rejectModal.userQuestion)}
              </p>
              <div className="d-flex gap-2 mt-3">
                <Button color="danger" onClick={handleRejectConfirm}>
                  Yes, reject and refund
                </Button>
                <Button color="secondary" onClick={() => setRejectModal(null)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Complete request: video + title (required), optional thumbnail, shortDesc, longDesc */}
      <Modal
        isOpen={!!completeModal}
        toggle={() => !completing && setCompleteModal(null)}
        size="lg"
      >
        <ModalHeader toggle={() => !completing && setCompleteModal(null)}>
          Complete request (video + title required)
        </ModalHeader>
        <ModalBody>
          {completeModal && (
            <form onSubmit={handleCompleteSubmit}>
              <p className="text-muted small mb-2">
                User: {completeModal.userName || '—'} ({completeModal.userEmail || '—'})
              </p>
              <div className="alert alert-light border small mb-3">
                <strong>User question:</strong>
                <div className="mt-1">{renderUserQuestion(completeModal.userQuestion)}</div>
              </div>

              <FormGroup>
                <Label>Title (required)</Label>
                <Input
                  type="text"
                  placeholder="e.g. March 2025 Reading"
                  value={completeTitle}
                  onChange={(e) => setCompleteTitle(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Video file (required) – MP4, MOV, AVI or audio</Label>
                <Input
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav"
                  onChange={(e) => setCompleteVideoFile(e.target.files?.[0] ?? null)}
                />
                {completeVideoFile && (
                  <small className="text-muted d-block mt-1">Selected: {completeVideoFile.name}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Thumbnail (optional) – image</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => setCompleteThumbnailFile(e.target.files?.[0] ?? null)}
                />
                {completeThumbnailFile && (
                  <small className="text-muted d-block mt-1">Selected: {completeThumbnailFile.name}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Short description (optional)</Label>
                <Input
                  type="textarea"
                  placeholder="Brief summary"
                  value={completeShortDesc}
                  onChange={(e) => setCompleteShortDesc(e.target.value)}
                  rows={2}
                />
              </FormGroup>

              <FormGroup>
                <Label>Long description (optional)</Label>
                <Input
                  type="textarea"
                  placeholder="Full description or notes"
                  value={completeLongDesc}
                  onChange={(e) => setCompleteLongDesc(e.target.value)}
                  rows={3}
                />
              </FormGroup>

              <div className="d-flex gap-2 mt-3">
                <Button
                  color="primary"
                  type="submit"
                  disabled={completing || !completeVideoFile || !completeTitle.trim()}
                >
                  {completing ? 'Completing...' : 'Complete and notify user'}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  onClick={() => setCompleteModal(null)}
                  disabled={completing}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>

      {/* Edit details (patch) – delivered orders only */}
      <Modal
        isOpen={!!patchModal}
        toggle={() => !patching && setPatchModal(null)}
        size="lg"
      >
        <ModalHeader toggle={() => !patching && setPatchModal(null)}>
          Edit order details
        </ModalHeader>
        <ModalBody>
          {patchModal && (
            <form onSubmit={handlePatchSubmit}>
              <p className="text-muted small mb-2">
                User: {patchModal.userName || '—'} ({patchModal.userEmail || '—'})
              </p>
              <div className="alert alert-light border small mb-3">
                <strong>User question:</strong>
                <div className="mt-1">{renderUserQuestion(patchModal.userQuestion)}</div>
              </div>

              <FormGroup>
                <Label>Title (required)</Label>
                <Input
                  type="text"
                  placeholder="e.g. March 2025 Reading"
                  value={patchTitle}
                  onChange={(e) => setPatchTitle(e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Video (optional) – new file replaces current</Label>
                <Input
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav"
                  onChange={(e) => setPatchVideoFile(e.target.files?.[0] ?? null)}
                />
                {patchVideoFile && (
                  <>
                    <small className="text-muted d-block mt-1">
                      Selected: {patchVideoFile.name}
                    </small>
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">New video preview:</small>
                      <video
                        controls
                        style={{ maxWidth: '100%', maxHeight: 240 }}
                        src={URL.createObjectURL(patchVideoFile)}
                      />
                    </div>
                  </>
                )}
                {!patchVideoFile && patchModal.fileUrl && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-1">Current video:</small>
                    <video
                      controls
                      style={{ maxWidth: '100%', maxHeight: 240 }}
                      src={patchModal.fileUrl}
                    />
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Thumbnail (optional) – new image replaces current</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => setPatchThumbnailFile(e.target.files?.[0] ?? null)}
                />
                {patchThumbnailFile && (
                  <>
                    <small className="text-muted d-block mt-1">
                      Selected: {patchThumbnailFile.name}
                    </small>
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">New thumbnail preview:</small>
                      <img
                        src={URL.createObjectURL(patchThumbnailFile)}
                        alt="New thumbnail preview"
                        style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8 }}
                      />
                    </div>
                  </>
                )}
                {!patchThumbnailFile && patchModal.thumbnailUrl && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-1">Current thumbnail:</small>
                    <img
                      src={patchModal.thumbnailUrl}
                      alt="Current thumbnail"
                      style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8 }}
                    />
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Short description (optional)</Label>
                <Input
                  type="textarea"
                  placeholder="Brief summary"
                  value={patchShortDesc}
                  onChange={(e) => setPatchShortDesc(e.target.value)}
                  rows={2}
                />
              </FormGroup>

              <FormGroup>
                <Label>Long description (optional)</Label>
                <Input
                  type="textarea"
                  placeholder="Full description or notes"
                  value={patchLongDesc}
                  onChange={(e) => setPatchLongDesc(e.target.value)}
                  rows={3}
                />
              </FormGroup>

              <div className="d-flex gap-2 mt-3">
                <Button
                  color="primary"
                  type="submit"
                  disabled={patching || !patchTitle.trim()}
                >
                  {patching ? 'Saving...' : 'Save changes'}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  onClick={() => setPatchModal(null)}
                  disabled={patching}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default OracleReadingPage;
